import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { AlertCircle } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api.config';

export interface BillingAddress {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    name?: string;
    phone?: string;
}

interface BillingAddressResponse {
    data: BillingAddress;
}

const BillingAddressForm = () => {
    const [address, setAddress] = useState<BillingAddress>({
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        name: '',
        phone: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchBillingAddress();
    }, []);

    const fetchBillingAddress = async () => {
        try {
            setLoading(true);
            const response = await apiService.get<BillingAddressResponse>(API_ENDPOINTS.BILLING.ADDRESS.GET);
            console.log('Billing Address Response:', response);

            if (response?.data) {
                setAddress({
                    line1: response.data.line1 || '',
                    line2: response.data.line2 || '',
                    city: response.data.city || '',
                    state: response.data.state || '',
                    postal_code: response.data.postal_code || '',
                    country: response.data.country || '',
                    name: response.data.name || '',
                    phone: response.data.phone || ''
                });
            }
        } catch (err) {
            console.error('Error fetching billing address:', err);
            setError('Failed to load billing address');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        try {
            setLoading(true);
            const response = await apiService.put<BillingAddressResponse>(
                API_ENDPOINTS.BILLING.ADDRESS.UPDATE,
                address
            );

            if (response?.data) {
                setAddress(response.data);
                setSuccess(true);
            }
        } catch (err) {
            console.error('Error updating billing address:', err);
            setError('Failed to update billing address');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setAddress(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (loading && !address.line1) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4 text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                <h6 className="mb-4">Billing Address</h6>

                {error && (
                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                        <AlertCircle size={20} className="me-2" />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="alert alert-success" role="alert">
                        Billing address updated successfully!
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label htmlFor="name" className="form-label">Full Name</label>
                        <input
                            type="text"
                            className="form-control"
                            id="name"
                            name="name"
                            value={address.name || ''}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="line1" className="form-label">Address Line 1</label>
                        <input
                            type="text"
                            className="form-control"
                            id="line1"
                            name="line1"
                            value={address.line1}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="line2" className="form-label">Address Line 2 (Optional)</label>
                        <input
                            type="text"
                            className="form-control"
                            id="line2"
                            name="line2"
                            value={address.line2 || ''}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label htmlFor="city" className="form-label">City</label>
                            <input
                                type="text"
                                className="form-control"
                                id="city"
                                name="city"
                                value={address.city}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="state" className="form-label">State</label>
                            <input
                                type="text"
                                className="form-control"
                                id="state"
                                name="state"
                                value={address.state}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label htmlFor="postal_code" className="form-label">Postal Code</label>
                            <input
                                type="text"
                                className="form-control"
                                id="postal_code"
                                name="postal_code"
                                value={address.postal_code}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="country" className="form-label">Country</label>
                            <input
                                type="text"
                                className="form-control"
                                id="country"
                                name="country"
                                value={address.country}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="phone" className="form-label">Phone Number</label>
                        <input
                            type="tel"
                            className="form-control"
                            id="phone"
                            name="phone"
                            value={address.phone || ''}
                            onChange={handleChange}
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Saving...
                            </>
                        ) : (
                            'Save Address'
                        )}
                    </button>
                </form>
            </Card.Body>
        </Card>
    );
};

export default BillingAddressForm;